import React, { Suspense, type ReactNode } from "react";
import {
  At,
  Paperclip,
  PaperPlaneRight,
  Smiley,
  TextAa,
} from "@/components/ui/icons";
import { Button } from "@/components/ui/button";
import { IconTooltip } from "@/components/ui/icon-tooltip";
import { cn } from "@/lib/utils";
import { POPOVER_SURFACE_CLASSES } from "@/lib/surface-styles";

const LazyEmojiPicker = React.lazy(() => import("../EmojiPicker"));

interface MessageInputToolbarProps {
  isBusy: boolean;
  showFormatting: boolean;
  toggleFormatting: () => void;
  showEmojiPicker: boolean;
  toggleEmojiPicker: () => void;
  closeEmojiPicker: () => void;
  insertEmoji: (emoji: string) => void;
  openFilePicker: () => void;
  triggerMention: () => void;
  hasContent: boolean;
  onSend: () => void;
}

const TOOLBAR_ICON =
  "h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground";

function ToolbarButton({
  label,
  onClick,
  disabled,
  className,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <IconTooltip label={label} side="top">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onClick}
        disabled={disabled}
        className={className}
        aria-label={label}
      >
        {children}
      </Button>
    </IconTooltip>
  );
}

/**
 * Bottom toolbar of the composer: Format / Emoji / Attach / Mention on
 * the left, Send button on the right. Emoji picker is lazy-loaded.
 */
export function MessageInputToolbar({
  isBusy,
  showFormatting,
  toggleFormatting,
  showEmojiPicker,
  toggleEmojiPicker,
  closeEmojiPicker,
  insertEmoji,
  openFilePicker,
  triggerMention,
  hasContent,
  onSend,
}: MessageInputToolbarProps) {
  return (
    <div className="flex items-center justify-between px-2 pb-1.5">
      <div className="flex items-center gap-0.5">
        <ToolbarButton
          label={showFormatting ? "Hide formatting" : "Show formatting"}
          onClick={toggleFormatting}
          disabled={isBusy}
          className={cn(
            TOOLBAR_ICON,
            showFormatting && "text-foreground hover:text-foreground",
          )}
        >
          <TextAa size={16} />
        </ToolbarButton>
        <div className="relative">
          <ToolbarButton
            label="Emoji"
            onClick={toggleEmojiPicker}
            disabled={isBusy}
            className={TOOLBAR_ICON}
          >
            <Smiley size={16} />
          </ToolbarButton>
          {showEmojiPicker && (
            <Suspense
              fallback={
                <div
                  className={cn(
                    POPOVER_SURFACE_CLASSES,
                    "bottom-9 left-0 px-2 py-1 text-xs text-muted-foreground",
                  )}
                >
                  Loading emoji picker...
                </div>
              }
            >
              <LazyEmojiPicker
                onSelect={insertEmoji}
                onClose={closeEmojiPicker}
              />
            </Suspense>
          )}
        </div>
        <ToolbarButton
          label="Attach file"
          onClick={openFilePicker}
          disabled={isBusy}
          className={TOOLBAR_ICON}
        >
          <Paperclip size={16} />
        </ToolbarButton>
        <ToolbarButton
          label="Mention someone"
          onClick={triggerMention}
          disabled={isBusy}
          className={TOOLBAR_ICON}
        >
          <At size={16} />
        </ToolbarButton>
      </div>

      <IconTooltip label="Send message (↵)" side="top">
        <Button
          type="button"
          variant={hasContent ? "default" : "ghost"}
          size="icon"
          onClick={onSend}
          disabled={isBusy}
          className={cn(!hasContent && "text-muted-foreground")}
          aria-label="Send message"
        >
          <PaperPlaneRight size={16} />
        </Button>
      </IconTooltip>
    </div>
  );
}
